import type { ReactNode } from "react";
import { Input, RadioGroup } from "../../components/ui";
import { formatOffset, formatUtc, parseLocalInput, resolveLocal, viewerTimeZone } from "../../lib/datetime";

// Schedule entry, shared by the wizard (UX-DESIGN.md §2.3 step 1) and the
// status page's schedule edit (§2.4), so the DST rule is described and
// enforced once: a skipped local time is rejected, a repeated one forces
// an explicit choice of offset. Never a silent "nearest valid" instant.

export type TimeChoice = "earlier" | "later" | "";

export type ResolvedTime = ({ ok: true; instant: Date | null } | { ok: false; error: string }) & { ambiguous?: [Date, Date] };

export function resolveField(local: string, choice: TimeChoice, required: boolean): ResolvedTime {
  if (!local) return required ? { ok: false, error: "Choose a date and time." } : { ok: true, instant: null };
  const parts = parseLocalInput(local);
  if (!parts) return { ok: false, error: "Enter a valid date and time." };
  const r = resolveLocal(parts);
  switch (r.kind) {
    case "ok":
      return { ok: true, instant: r.instant };
    case "skipped":
      return {
        ok: false,
        error: `That time doesn't exist in ${viewerTimeZone()} (clocks skip it for daylight saving). Choose a different time.`,
      };
    case "ambiguous":
      if (choice === "earlier") return { ok: true, instant: r.candidates[0], ambiguous: r.candidates };
      if (choice === "later") return { ok: true, instant: r.candidates[1], ambiguous: r.candidates };
      return { ok: false, error: "That time happens twice (clocks go back). Choose which one you mean.", ambiguous: r.candidates };
    case "invalid":
      return { ok: false, error: "Enter a valid date and time." };
  }
}

/** "2030-01-15T09:00" for a datetime-local input, in the viewer's zone. */
export function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function TimeField({
  label,
  value,
  onChange,
  resolved,
  choice,
  onChoice,
  error,
  hint,
  optional,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  resolved: ResolvedTime;
  choice: TimeChoice;
  onChoice: (c: TimeChoice) => void;
  error?: string;
  hint?: ReactNode;
  optional?: boolean;
  disabled?: boolean;
}) {
  const instant = resolved.ok ? resolved.instant : null;
  return (
    <div className="stack-sm">
      <Input
        label={label}
        type="datetime-local"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        error={error}
        optional={optional}
        disabled={disabled}
        hint={
          instant ? (
            <>
              Stored as <code>{formatUtc(instant)}</code>
            </>
          ) : (
            hint
          )
        }
      />
      {resolved.ambiguous ? (
        <RadioGroup<"earlier" | "later">
          legend="Which one?"
          name={`${label}-offset`}
          value={choice}
          onChange={onChoice}
          options={[
            { value: "earlier", label: `First occurrence (${formatOffset(resolved.ambiguous[0])})`, hint: formatUtc(resolved.ambiguous[0]) },
            { value: "later", label: `Second occurrence (${formatOffset(resolved.ambiguous[1])})`, hint: formatUtc(resolved.ambiguous[1]) },
          ]}
        />
      ) : null}
    </div>
  );
}
